import type {
  ProductionSelectionValidation,
  ProductionSuggestion,
} from '../../../types/restaurant/RestaurantProductionPlannerTypes';
import {
  addDecimal,
  compareDecimal,
  multiplyDecimal,
} from '../../../utils/decimalValue';

export const validateProductionSelection = (
  suggestions: ProductionSuggestion[],
  quantities: Record<number, string>
): ProductionSelectionValidation => {
  const selected = suggestions
    .map((suggestion) => ({
      suggestion,
      quantity: quantities[suggestion.recipeId]?.trim() ?? '',
    }))
    .filter((item) => item.quantity !== '');

  if (!selected.length)
    return {
      valid: false,
      message: 'Ingrese una cantidad para al menos una receta.',
      selectedItems: [],
      estimatedTotalCost: '0',
    };

  for (const item of selected) {
    if (compareDecimal(item.quantity, 0) <= 0)
      return {
        valid: false,
        message: `La cantidad de ${item.suggestion.productName} debe ser mayor que cero.`,
        selectedItems: [],
        estimatedTotalCost: '0',
      };
    if (!item.suggestion.producible)
      return {
        valid: false,
        message: `${item.suggestion.productName} no está disponible para producción.`,
        selectedItems: [],
        estimatedTotalCost: '0',
      };
    if (
      compareDecimal(item.quantity, item.suggestion.maxProducibleQuantity) > 0
    )
      return {
        valid: false,
        message: `${item.suggestion.productName} supera el máximo fabricable.`,
        selectedItems: [],
        estimatedTotalCost: '0',
      };
  }

  const aggregate = new Map<
    number,
    { name: string; required: string; available: string; unitCode: string }
  >();
  selected.forEach(({ suggestion, quantity }) =>
    suggestion.ingredients.forEach((ingredient) => {
      const current = aggregate.get(ingredient.rawMaterialId);
      const required = multiplyDecimal(quantity, ingredient.requiredPerUnit);
      aggregate.set(ingredient.rawMaterialId, {
        name: ingredient.rawMaterialName,
        required: addDecimal(current?.required ?? '0', required),
        available: String(ingredient.availableQuantity),
        unitCode: ingredient.unitCode,
      });
    })
  );
  const insufficient = [...aggregate.values()].find(
    (item) => compareDecimal(item.required, item.available) > 0
  );
  if (insufficient)
    return {
      valid: false,
      message: `La selección conjunta requiere más ${insufficient.name} del disponible (${insufficient.available} ${insufficient.unitCode}).`,
      selectedItems: [],
      estimatedTotalCost: '0',
    };

  return {
    valid: true,
    message: null,
    selectedItems: selected.map(({ suggestion, quantity }) => ({
      recipeId: suggestion.recipeId,
      quantity,
    })),
    estimatedTotalCost: selected.reduce(
      (total, { suggestion, quantity }) =>
        addDecimal(
          total,
          multiplyDecimal(quantity, suggestion.estimatedUnitCost)
        ),
      '0'
    ),
  };
};
